import { Children, isValidElement, useEffect, useRef, useState } from 'react';
import type { ImgHTMLAttributes, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Components } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { VideoEmbed, isVideoUrl } from './video';

type Point = { x: number; y: number };
type View = Point & { scale: number };
const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

// Счётчик сохраняет блокировку при вложенном просмотре фото.
let locks = 0;
let bodyOverflow = '';
function Modal({ children, onClose, className = '' }: { children: ReactNode; onClose: () => void; className?: string }) {
  const root = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    if (locks++ === 0) { bodyOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; }
    const element = root.current!;
    element.focus();
    const keydown = (e: KeyboardEvent) => {
      if (!element.contains(document.activeElement)) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); close.current(); }
      if (e.key === 'Tab') {
        const items = Array.from(element.querySelectorAll<HTMLElement>('button, a[href], input, iframe, [tabindex="0"]')).filter(item => !item.hasAttribute('disabled'));
        const first = items[0], last = items[items.length - 1];
        if (!first) { e.preventDefault(); return; }
        if (e.shiftKey && (document.activeElement === first || document.activeElement === element)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || document.activeElement === element)) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      if (--locks === 0) document.body.style.overflow = bodyOverflow;
      previous?.focus();
    };
  }, []);
  return createPortal(<div ref={root} role="dialog" aria-modal="true" aria-label="Просмотр изображения и подсказок" tabIndex={-1}
    className={`fixed inset-0 z-[1000] bg-black/90 text-white outline-none print:hidden ${className}`}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    {children}
    <button type="button" onClick={onClose} aria-label="Закрыть" className="absolute right-4 top-4 z-20 rounded-full bg-black/70 px-4 py-3 text-white">✕</button>
  </div>, document.body);
}

function LightboxView({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  const [view, setView] = useState<View>({ x: 0, y: 0, scale: 1 });
  const current = useRef(view);
  const surface = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, Point>());
  const lastTap = useRef({ time: 0, x: 0, y: 0 });
  const start = useRef<Point | null>(null);
  const moved = useRef(false);
  const background = useRef(false);
  const update = (v: View) => { current.current = v; setView(v); };
  const local = (x: number, y: number): Point => {
    const rect = surface.current!.getBoundingClientRect();
    return { x: x - rect.left - rect.width / 2, y: y - rect.top - rect.height / 2 };
  };
  const zoom = (scale: number, p: Point) => {
    const v = current.current, next = clamp(scale, 1, 4), ratio = next / v.scale;
    update(next === 1 ? { x: 0, y: 0, scale: 1 } : { x: p.x - (p.x - v.x) * ratio, y: p.y - (p.y - v.y) * ratio, scale: next });
  };
  useEffect(() => {
    const element = surface.current!;
    const wheel = (e: WheelEvent) => { e.preventDefault(); zoom(current.current.scale * (e.deltaY < 0 ? 2 : 0.5), local(e.clientX, e.clientY)); };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  }, []);
  const release = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.type === 'pointerup' && pointers.current.size === 1 && !moved.current) {
      const now = Date.now(), tap = lastTap.current;
      if (now - tap.time < 320 && Math.hypot(e.clientX - tap.x, e.clientY - tap.y) < 25) {
        zoom(current.current.scale < 4 ? current.current.scale * 2 : 1, local(e.clientX, e.clientY));
        lastTap.current.time = 0;
      } else lastTap.current = { time: now, x: e.clientX, y: e.clientY };
    }
    pointers.current.delete(e.pointerId);
  };
  return <Modal onClose={onClose}>
    <div ref={surface} className="absolute inset-x-0 top-16 bottom-20 flex items-center justify-center overflow-hidden touch-none"
      onClick={e => { if (background.current && e.target === e.currentTarget && !moved.current) onClose(); }}
      onPointerDown={e => {
        if (e.button !== 0) return;
        background.current = e.target === e.currentTarget;
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        start.current = { x: e.clientX, y: e.clientY };
        moved.current = pointers.current.size > 1;
        // Захват нужен только на картинке: фон остаётся кнопкой закрытия.
        if (e.target !== e.currentTarget) e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={e => {
        const old = pointers.current.get(e.pointerId);
        if (!old) return;
        const before = [...pointers.current.values()];
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 5) moved.current = true;
        const after = [...pointers.current.values()], v = current.current;
        if (before.length === 2) {
          const midpoint = (points: Point[]) => local((points[0].x + points[1].x) / 2, (points[0].y + points[1].y) / 2);
          const distance = (points: Point[]) => Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
          const a = midpoint(before), b = midpoint(after);
          const scale = clamp(v.scale * distance(after) / Math.max(1, distance(before)), 1, 4);
          update(scale === 1 ? { x: 0, y: 0, scale } : { x: b.x - (a.x - v.x) * scale / v.scale, y: b.y - (a.y - v.y) * scale / v.scale, scale });
        } else if (v.scale > 1) update({ ...v, x: v.x + e.clientX - old.x, y: v.y + e.clientY - old.y });
      }} onPointerUp={release} onPointerCancel={release} onLostPointerCapture={e => pointers.current.delete(e.pointerId)}>
      <img src={src} alt={alt} draggable={false} className="max-h-full max-w-full select-none object-contain"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, cursor: view.scale > 1 ? 'grab' : 'zoom-in' }} />
    </div>
    <div className="absolute inset-x-4 bottom-3 text-center">
      {alt && <p className="m-0 text-sm">{alt}</p>}
      <button type="button" className="mt-2 rounded bg-white/20 px-3 py-1" onClick={() => update({ x: 0, y: 0, scale: 1 })}>Сбросить масштаб ({view.scale.toFixed(1)}×)</button>
    </div>
  </Modal>;
}

export function ImageLightbox({ src = '', alt = '', ...props }: ImgHTMLAttributes<HTMLImageElement>) {
  const [open, setOpen] = useState(false);
  return <span className="block my-4">
    <button type="button" className="relative block max-w-full mx-auto cursor-zoom-in" aria-label={`Увеличить: ${alt || 'изображение'}`} onClick={() => setOpen(true)}>
      {/* 17.09.2026: высота до 280 точек, как на страницах статей и уроков сайта.
          Щелчок по-прежнему открывает картинку в полный размер. */}
      <img {...props} src={src} alt={alt} className="max-w-full max-h-[280px] w-auto h-auto rounded-xl mx-auto" loading="lazy" />
      <span aria-hidden="true" className="absolute right-2 top-2 rounded bg-black/60 px-2 text-white print:hidden">⤢</span>
    </button>
    {alt && <span className="block text-center text-sm text-[var(--lesson-muted,#626B73)] mt-2">{alt}</span>}
    {open && <LightboxView src={src} alt={alt} onClose={() => setOpen(false)} />}
  </span>;
}

// x,y — где стоит кружок; tx,ty — необязательный конец линии-выноски на самой детали (09.09.2026)
// x,y — кружок; tx,ty — конец одной линии; targets — несколько концов, если деталь на рисунке в нескольких местах
type Spot = { x: number; y: number; tx?: number; ty?: number; targets?: [number, number][]; title: string; text?: string; photo?: string; video?: string };
type Hints = { image: string; alt?: string; spots: Spot[] };
type Comparison = { before: string; after: string; labels?: [string, string]; caption?: string };
const nonempty = (v: unknown): v is string => typeof v === 'string' && !!v.trim();
const optionalText = (v: unknown) => v === undefined || typeof v === 'string';
function validHints(v: any): v is Hints {
  return !!v && nonempty(v.image) && optionalText(v.alt) && Array.isArray(v.spots) && v.spots.length >= 1 && v.spots.length <= 20
    && v.spots.every((s: any) => s && Number.isFinite(s.x) && s.x >= 0 && s.x <= 100 && Number.isFinite(s.y) && s.y >= 0 && s.y <= 100
      && (s.tx === undefined || (Number.isFinite(s.tx) && s.tx >= 0 && s.tx <= 100))
      && (s.ty === undefined || (Number.isFinite(s.ty) && s.ty >= 0 && s.ty <= 100))
      && (s.targets === undefined || (Array.isArray(s.targets) && s.targets.length <= 6
        && s.targets.every((t: any) => Array.isArray(t) && t.length === 2 && t.every((v: any) => Number.isFinite(v) && v >= 0 && v <= 100))))
      && nonempty(s.title) && [s.text, s.photo, s.video].every(optionalText));
}
function validComparison(v: any): v is Comparison {
  return !!v && nonempty(v.before) && nonempty(v.after) && optionalText(v.caption)
    && (v.labels === undefined || (Array.isArray(v.labels) && v.labels.length === 2 && v.labels.every(nonempty)));
}
function interactiveLanguage(className?: string): 'hints' | 'beforeafter' | undefined {
  return className?.split(/\s+/).includes('language-hints') ? 'hints' : className?.split(/\s+/).includes('language-beforeafter') ? 'beforeafter' : undefined;
}
function LessonBlock({ language, source }: { language: 'hints' | 'beforeafter'; source: string }) {
  try {
    const data: unknown = JSON.parse(source);
    if (language === 'hints' && validHints(data)) return <PhotoHints {...data} />;
    if (language === 'beforeafter' && validComparison(data)) return <BeforeAfter {...data} />;
    console.warn(`Блок ${language}: неверные поля`);
  } catch { console.warn(`Блок ${language}: неверный JSON`); }
  return null;
}

export function PhotoHints({ image, alt = '', spots }: Hints) {
  const [active, setActive] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origin = useRef<Point | null>(null);
  const cancel = () => { if (timer.current) clearTimeout(timer.current); timer.current = null; };
  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const change = () => setMobile(media.matches);
    change(); media.addEventListener('change', change);
    return () => { cancel(); media.removeEventListener('change', change); };
  }, []);
  const detail = active === null ? null : <div className="p-5">
    <h3 className="mt-0">{spots[active].title}</h3>
    {spots[active].text && <p>{spots[active].text}</p>}
    {spots[active].photo && <ImageLightbox src={spots[active].photo} alt={spots[active].title} />}
    {spots[active].video && <VideoEmbed url={spots[active].video} />}
    <button type="button" onClick={() => setActive(null)} className="rounded border px-3 py-2">Закрыть подсказку</button>
  </div>;
  return <section className="my-4" aria-label={alt || 'Подсказки на фото'}>
    <div className="relative mx-auto w-fit max-w-full">
      <img src={image} alt={alt} loading="lazy" draggable={false} className="m-0 block max-w-full h-auto rounded-xl select-none"
        onContextMenu={e => e.preventDefault()}
        onPointerDown={e => { cancel(); origin.current = { x: e.clientX, y: e.clientY }; timer.current = setTimeout(() => { setOpen(true); cancel(); }, 550); }}
        onPointerMove={e => { if (origin.current && Math.hypot(e.clientX - origin.current.x, e.clientY - origin.current.y) > 8) cancel(); }}
        onPointerUp={cancel} onPointerCancel={cancel} onPointerLeave={cancel} />
      <button type="button" aria-label="Увеличить изображение" onClick={() => setOpen(true)} className="absolute top-2 right-2 rounded bg-black/70 px-3 py-2 text-white print:hidden">⤢</button>
      {/* линии-выноски: от кружка к точке на детали (там, где заданы tx/ty) */}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full">
        {spots.map((spot, index) => {
          const ends: [number, number][] = spot.targets?.length ? spot.targets
            : spot.tx !== undefined && spot.ty !== undefined ? [[spot.tx, spot.ty]] : [];
          const colour = active === index ? '#f59e0b' : '#0f766e';
          return ends.map(([tx, ty], k) => (
            <g key={`${index}-${k}`}>
              <line x1={spot.x} y1={spot.y} x2={tx} y2={ty} stroke="#ffffff" strokeWidth={3.5} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
              <line x1={spot.x} y1={spot.y} x2={tx} y2={ty} stroke={colour} strokeWidth={1.8} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
              <line x1={tx} y1={ty} x2={tx} y2={ty} stroke={colour} strokeWidth={7} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
            </g>
          ));
        })}
      </svg>
      {spots.map((spot, index) => <button type="button" key={index} aria-label={`${index + 1}. ${spot.title}`} aria-pressed={active === index}
        onClick={() => setActive(index)} style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
        className={`absolute -translate-x-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full border-2 border-white font-bold shadow ${active === index ? 'bg-amber-400 text-black' : 'bg-teal-700 text-white'}`}>{index + 1}</button>)}
    </div>
    {active !== null && (mobile ? <Modal onClose={() => setActive(null)} className="bg-black/50">
      <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white text-gray-900">{detail}</div>
    </Modal> : <div className="mt-3 rounded-xl border border-gray-300 print:hidden">{detail}</div>)}
    <ol>{spots.map((spot, index) => <li key={index}>{spot.title}{spot.text ? ` — ${spot.text}` : ''}</li>)}</ol>
    {open && <LightboxView src={image} alt={alt} onClose={() => setOpen(false)} />}
  </section>;
}

export function BeforeAfter({ before, after, labels = ['до', 'после'], caption }: Comparison) {
  const [position, setPosition] = useState(50);
  const [opened, setOpened] = useState<'before' | 'after' | null>(null);
  const frame = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  const tap = useRef({ time: 0, x: 0, y: 0 });
  const valueAt = (x: number) => { const rect = frame.current!.getBoundingClientRect(); return clamp((x - rect.left) / rect.width * 100, 0, 100); };
  return <figure className="my-4">
    <div ref={frame} className="relative overflow-hidden rounded-xl touch-none select-none"
      onPointerDown={e => { if (e.button !== 0) return; e.currentTarget.setPointerCapture(e.pointerId); drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false }; }}
      onPointerMove={e => { const d = drag.current; if (d?.id === e.pointerId && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 4) { d.moved = true; setPosition(valueAt(e.clientX)); } }}
      onPointerUp={e => {
        const d = drag.current;
        if (d?.id !== e.pointerId) return;
        if (!d.moved) {
          const now = Date.now(), last = tap.current;
          if (now - last.time < 320 && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 25) {
            setOpened(valueAt(e.clientX) < position ? 'before' : 'after'); tap.current.time = 0;
          } else tap.current = { time: now, x: e.clientX, y: e.clientY };
        }
        drag.current = null;
      }} onPointerCancel={() => { drag.current = null; }} onLostPointerCapture={() => { drag.current = null; }}>
      <img src={after} alt={labels[1]} draggable={false} className="m-0 block w-full h-auto" />
      <img src={before} alt={labels[0]} draggable={false} className="absolute inset-0 m-0 h-full w-full object-cover" style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }} />
      <span className="pointer-events-none absolute left-2 top-2 rounded bg-black/60 px-2 text-white">{labels[0]}</span>
      <span className="pointer-events-none absolute right-2 top-2 rounded bg-black/60 px-2 text-white">{labels[1]}</span>
      <div role="slider" tabIndex={0} aria-label="Граница до и после" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(position)} aria-orientation="horizontal"
        onKeyDown={e => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
            e.preventDefault(); setPosition(p => e.key === 'Home' ? 0 : e.key === 'End' ? 100 : clamp(p + (e.key === 'ArrowLeft' ? -1 : 1), 0, 100));
          }
        }} className="absolute inset-y-0 w-1 -translate-x-1/2 bg-white cursor-ew-resize focus:outline focus:outline-2 focus:outline-teal-500" style={{ left: `${position}%` }}>
        <span aria-hidden="true" className="absolute top-1/2 left-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-900 shadow">↔</span>
      </div>
    </div>
    <input type="range" min={0} max={100} value={position} onChange={e => setPosition(Number(e.target.value))} aria-label="Положение границы до и после" className="mt-3 w-full" />
    <div className="flex justify-between text-sm print:hidden">
      <button type="button" onClick={() => setOpened('before')}>Увеличить «{labels[0]}»</button>
      <button type="button" onClick={() => setOpened('after')}>Увеличить «{labels[1]}»</button>
    </div>
    {caption && <figcaption className="text-center text-sm">{caption}</figcaption>}
    {opened && <LightboxView src={opened === 'before' ? before : after} alt={`${labels[opened === 'before' ? 0 : 1]}${caption ? ` — ${caption}` : ''}`} onClose={() => setOpened(null)} />}
  </figure>;
}

export function lessonMarkdownComponents(): Components {
  return {
    // Обработка ссылок — автоопределение видео
    a({ href, node, children, ...props }: any) {
      // Если ссылка — это видео, показываем плеер
      if (href && isVideoUrl(href)) {
        return <VideoEmbed url={href} />;
      }
      // Если текст ссылки совпадает с URL и это видео
      const childText = String(children);
      if (childText && isVideoUrl(childText)) {
        return <VideoEmbed url={childText} />;
      }
      // Обычная ссылка
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-[var(--lesson-link,#0F766E)] hover:underline" {...props}>
          {children}
        </a>
      );
    },
    // Параграф — проверяем на видео-ссылку
    p({ node, children, ...props }: any) {
      // Если в параграфе только одна строка-ссылка на видео
      if (typeof children === 'string' && isVideoUrl(children.trim())) {
        return <VideoEmbed url={children.trim()} />;
      }
      // Проверяем массив children
      if (Array.isArray(children) && children.length === 1) {
        const child = children[0];
        if (typeof child === 'string' && isVideoUrl(child.trim())) {
          return <VideoEmbed url={child.trim()} />;
        }
      }
      return <p {...props}>{children}</p>;
    },
    // Адаптивные картинки
    img({ node, src, alt, ...props }: any) {
      return <ImageLightbox src={src} alt={alt || ''} {...props} />;
    },
    // Таблицы с горизонтальным скроллом
    table({ node, children, ...props }: any) {
      return (
        <div className="overflow-x-auto -mx-4 px-4 my-4">
          <table className="min-w-full" {...props}>{children}</table>
        </div>
      );
    },
    th({ node, children, ...props }: any) {
      return (
        <th className="px-3 py-2 text-left text-sm font-semibold bg-[var(--lesson-bg,white)] border border-[var(--lesson-table-border,#E1E5E3)] whitespace-nowrap" {...props}>
          {children}
        </th>
      );
    },
    td({ node, children, ...props }: any) {
      return (
        <td className="px-3 py-2 text-sm border border-[var(--lesson-table-border,#E1E5E3)]" {...props}>
          {children}
        </td>
      );
    },
    // Подсветка кода
    code({ node, inline, className, children, ...props }: any) {
      if (!inline && interactiveLanguage(className)) {
        return <LessonBlock language={interactiveLanguage(className)!} source={String(children)} />;
      }
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={oneDark}
          language={match[1]}
          PreTag="div"
          className="rounded-lg !bg-[var(--lesson-bg,white)] border-[length:var(--lesson-code-border,1px)] border-[var(--lesson-border,#E1E5E3)]"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className="bg-[var(--lesson-bg,white)] border-[length:var(--lesson-code-border,1px)] border-[var(--lesson-border,#E1E5E3)] px-1.5 py-0.5 rounded text-[var(--lesson-link,#0F766E)]" {...props}>
          {children}
        </code>
      );
    },
    pre({ node, children, ...props }) {
      const code = node?.children?.find(child => child.type === 'element' && child.tagName === 'code');
      const classes = code && code.type === 'element' ? code.properties?.className : undefined;
      const special = interactiveLanguage(Array.isArray(classes) ? classes.join(' ') : String(classes || ''))
        || Children.toArray(children).some(child => isValidElement<{ className?: string }>(child) && interactiveLanguage(child.props.className));
      return special ? <>{children}</> : <pre {...props}>{children}</pre>;
    },

  };
}
